<template>
  <div class="flex gap-2" data-testid="otp-input">
    <input
      v-for="(_, i) in digits"
      :key="i"
      ref="inputs"
      v-model="digits[i]"
      type="text"
      inputmode="numeric"
      maxlength="1"
      autocomplete="one-time-code"
      :disabled="disabled"
      :aria-label="`Ciffer ${i + 1}`"
      class="h-12 w-10 rounded-sm border border-gray-300 text-center text-lg focus:border-blue-500 focus:outline-hidden disabled:bg-gray-100"
      @input="onInput(i)"
      @keydown.delete="onDelete(i)"
      @paste.prevent="onPaste"
    />
  </div>
</template>

<script>
const LENGTH = 6;

export default {
  name: "BaseOTPInput",

  props: {
    disabled: { type: Boolean, default: false },
    modelValue: { type: String, default: "" },
  },

  emits: ["complete", "update:modelValue"],

  data() {
    return { digits: Array(LENGTH).fill("") };
  },

  watch: {
    // Let the parent clear the boxes by resetting the bound value (e.g. after a
    // rejected code) — a blank model wipes the digits and refocuses the first
    // box. We only act on a blank value; non-blank changes are driven by typing.
    modelValue(value) {
      if (value === "") this.reset();
    },
  },

  methods: {
    code() {
      return this.digits.join("");
    },

    emitState() {
      const code = this.code();
      this.$emit("update:modelValue", code);

      if (code.length === LENGTH && this.digits.every((digit) => digit !== "")) {
        this.$emit("complete", code);
      }
    },

    onInput(index) {
      // Keep a single numeric digit per box.
      this.digits[index] = (this.digits[index] || "").replace(/\D/g, "").slice(-1);
      if (this.digits[index] && index < LENGTH - 1) this.focusInput(index + 1);
      this.emitState();
    },

    onDelete(index) {
      if (!this.digits[index] && index > 0) this.focusInput(index - 1);
    },

    onPaste(event) {
      const pasted = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, LENGTH).split("");
      // Start from a clean slate so a shorter paste can't leave stale digits behind.
      this.digits = Array(LENGTH).fill("");
      pasted.forEach((char, index) => {
        this.digits[index] = char;
      });
      this.focusInput(Math.min(pasted.length, LENGTH - 1));
      this.emitState();
    },

    focusInput(index) {
      this.$nextTick(() => this.$refs.inputs?.[index]?.focus());
    },

    reset() {
      this.digits = Array(LENGTH).fill("");
      this.focusInput(0);
    },
  },
};
</script>
